import { useSyncExternalStore, type ReactNode } from "react";
import { RefreshIcon } from "@/components/icons";
import { HatTextTooltip } from "@/components/tooltip";
import { appConfig } from "@/config";
import { appUpdateController, isBundleStale } from "./appUpdateController";
import styles from "./AppUpdateBanner.module.css";

type AppUpdateBannerProps = {
  collapsed?: boolean;
};

const message = "새 버전으로 업데이트해 주세요.";

// 내 번들이 오래됐을 때만 사이드바 상단에 표시하는 새로고침 안내다. 화면을 옮겨도 남아야 한다.
// 그래서 페이지 본문이 아니라 사이드바 배너 슬롯이 소유한다.
//
// 자동으로 새로고침하지 않는다. 업로드 폼·에피소드 리뷰처럼 입력 중인 화면을 알림 없이 교체하면
// 작업을 잃는다. 안내는 사용자가 누를 때까지 남고, 닫기 버튼도 없다. 데이터는 모듈 싱글턴
// 컨트롤러가 소유한다. 그래서 라우트를 옮겨도 폴링과 판정이 유지된다.
export function AppUpdateBanner({ collapsed = false }: AppUpdateBannerProps): ReactNode {
  const snapshot = useSyncExternalStore(
    appUpdateController.subscribe,
    appUpdateController.getSnapshot,
    appUpdateController.getSnapshot,
  );
  if (!isBundleStale(snapshot, appConfig.buildId)) {
    return null;
  }

  // 접힘 레일에는 문구가 들어가지 않는다. 그래서 배너 전체가 새로고침 버튼이 되고, 글자 대신
  // 새로고침 글리프를 표시한다.
  if (collapsed) {
    return (
      <HatTextTooltip text={`${message} 눌러서 새로고침합니다.`} placement="right" mode="plain">
        <button
          type="button"
          className={styles.banner}
          data-collapsed="true"
          onClick={reloadPage}
          aria-label={`${message} 새로고침`}
        >
          <span className={styles.railGlyph} aria-hidden="true">
            <RefreshIcon />
          </span>
        </button>
      </HatTextTooltip>
    );
  }

  // 펼친 상태는 두 단이다. 문장 한 줄 아래에 버튼을 끝 정렬한다. 220px 사이드바에서는 문장과
  // 버튼이 한 줄에 들어가지 않는다. 말줄임하면 문장의 뜻이 사라진다.
  return (
    <div className={styles.banner} role="status" data-collapsed="false">
      <span className={styles.labelRow}>
        <span className={styles.labelDot} aria-hidden="true" />
        <span className={styles.label}>{message}</span>
      </span>
      <div className={styles.actions}>
        <HatTextTooltip
          text="화면을 다시 불러옵니다. 입력 중인 내용은 사라집니다."
          placement="bottom"
          mode="plain"
        >
          <button type="button" className={styles.reload} onClick={reloadPage}>
            새로고침
          </button>
        </HatTextTooltip>
      </div>
    </div>
  );
}

function reloadPage(): void {
  window.location.reload();
}
